import { Content } from "@/ui";
import { specialisms_helper } from "@/helpers/specialisms_helper";
import { createTitle } from "@/functions/createTitle";
import { getRoute } from "@/functions/getRoute";
import * as additionalComponents from "./__components";
import { jobSearch } from "@/functions/jobSearch";
import unslug from "unslug";
import _ from "lodash";
import sn from "@/snippets";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

export default function SpecialismPage({
  content,
  prefix,
  title,
  seo,
  canonical,
  data,
  isBuilder = false,
}) {
  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title?.length > 0 ? seo.title : title}
            description={seo?.description?.length > 0 ? seo.description : title}
            canonical={seo?.canonical?.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <Content items={content} additionalComponents={additionalComponents} />
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  const pages = url_slugs.map((url_slug, k) => ({
    url_slug,
    label: unslug(url_slug),
    href: getRoute("specialisms", { url_slugs: url_slugs.slice(0, k + 1) }),
  }));

  pages.unshift({
    url_slug: "jobs",
    label: "Find a job",
    href: "/jobs/",
  });

  const [_page, prevPage] = [...pages].reverse();
  const page = specialisms_helper.find(_page.url_slug);

  if (!page) {
    return { notFound: true };
  }

  if (page.page_builder === true) {
    const joined = url_slugs.join("/");
    const prefix = `specialisms/${joined}`;
    const canonical = getRoute("specialisms", { url_slugs });

    return {
      props: {
        isBuilder: true,
        prefix,
        title: page.title ?? "",
        canonical,
        seo: page.seo ?? {},          
        data: page.content ?? [], 
      },
    };
  }

  const jobs = _.uniqBy(
    _.flatten(
      await Promise.all(
        (page?.show_related_jobs ?? []).map(async ({ query, location, radius }) =>
          jobSearch({
            query,
            location,
            radius: radius ? parseInt(radius, 10) : null,
          }),
        ),
      ),
    ),
    "id",
  );

  return {
    props: {
      meta: {
        title: createTitle(page.title, "Specialisms"),
      },
      content: [
        {
          id: "BreadcrumbNavigation",
          component: "BreadcrumbNavigation",
          props: { items: pages },
        },
        sn.header({ page, prevPage }),
        {
          id: "SpecialismPageContent",
          component: "SpecialismPageContent",
          props: {
            body: page.body ?? "",
            secondaryContent: page.secondary_content ?? "",
            consultants: page.consultants ?? "",
            collapsibleSectionTitle: page.collapsible_section_title ?? "",
            collapsibleSectionBody: page.collapsible_section_description ?? "",
            collapsibleSectionItems: page.collapsible_section_items ?? [],
            jobs,
          },
        },
      ],
    },
  };
}

export async function getStaticPaths() {
  // Start with all nested paths
  const allPaths = specialisms_helper.toNestedPaths();

  // Filter out pages that are builder-based so they can be driven by the builder runtime
  // We need to look up each path’s tail slug in the helper to check page_builder.
  const filteredPaths = allPaths.filter((p) => {
    const slugs = p.params.url_slugs;
    const tail = slugs[slugs.length - 1];
    const page = specialisms_helper.find(tail);
    return page?.page_builder !== true;
  });

  return {
    paths: filteredPaths,
    fallback: false,
  };
}
